import { infiniteQueryOptions, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { conversationsApi } from '../api/conversations-api';
import { lastMessageId, mergeIntoThread, type ThreadData } from '../lib/thread-cache';

import { conversationKeys } from './conversation-keys';

const PAGE_SIZE = 50;

/**
 * The thread starts at the latest page and scrolls back with `before`. Live messages are merged
 * into the cache by the socket (or the poll below); a refetch never runs on focus because it would
 * reload only the first page and drop the live tail.
 */
export function conversationMessagesQueryOptions(storeId: string, id: string) {
  return infiniteQueryOptions({
    queryKey: conversationKeys.messages(storeId, id),
    queryFn: ({ pageParam }) =>
      conversationsApi.messages(storeId, id, {
        limit: PAGE_SIZE,
        ...(pageParam ? { before: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: () => undefined,
    getPreviousPageParam: (oldest) => (oldest.hasMore ? oldest.items[0]?.id : undefined),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

export function useConversationMessages(storeId: string, id: string, polling: boolean) {
  const queryClient = useQueryClient();
  const query = useInfiniteQuery(conversationMessagesQueryOptions(storeId, id));
  const ready = query.isSuccess;

  // Without a socket, catch up with `after=<last id>` every 5 s.
  useEffect(() => {
    if (!polling || !ready) return;
    const key = conversationKeys.messages(storeId, id);
    const timer = setInterval(() => {
      const after = lastMessageId(queryClient.getQueryData<ThreadData>(key));
      void conversationsApi
        .messages(storeId, id, { limit: 100, ...(after ? { after } : {}) })
        .then((page) =>
          queryClient.setQueryData<ThreadData>(key, (data) => mergeIntoThread(data, page.items)),
        )
        .catch(() => undefined);
    }, 5_000);
    return () => clearInterval(timer);
  }, [polling, ready, storeId, id, queryClient]);

  return query;
}
